/**
 * ScopeBox.jsx — the grading scope, parked in the bottom-right corner of the
 * image it is measuring.
 *
 * Mount it inside a positioned element (the stage, a cell, the peek) and hand it
 * something drawable; it measures that and paints a waveform, an RGB parade or a
 * histogram, switchable from the three letters in its corner. Which one is
 * showing lives in the store (s.scope.kind), so it is remembered between photos.
 *
 * The box itself is inert to the pointer — only the switch takes clicks — so a
 * stage underneath can still be dragged through it.
 *
 * Whether the box exists at all is s.scope.on, which whoever mounts this owns:
 * there is deliberately no close button here, because the way back on belongs
 * next to the rest of the editor's controls.
 */

import { useEffect, useRef } from 'react';
import { useMobile } from '../../hooks/useMobile';
import { setScope, useCarousel } from '../../hooks/useCarouselStore';
import { SCOPE_KINDS, computeScope, draw, isPixels, normalizeKind, scopeLabel } from '../../lib/carousel/scope.js';
import './scope.css';

const BOX = { w: 150, h: 90 };
const BOX_MOBILE = { w: 108, h: 65 };

/**
 * @param {object} p
 * @param {*} p.source     canvas, image, ImageBitmap or ImageData to measure
 * @param {*} [p.watch]    any value that changes when the source's pixels do
 * @param {boolean} [p.live]  re-measure every animation frame (during a drag)
 * @param {string} [p.filter] CSS filter the preview paints with, measured too
 * @param {object} [p.rect]   {x,y,w,h} region of the source to measure
 * @param {boolean} [p.mobile] force the phone's smaller box (default: the viewport)
 * @param {string} [p.className] e.g. 'is-flow' to drop the corner positioning
 */
export default function ScopeBox({ source, watch, live = false, filter = '', rect = null, mobile, className = '' }) {
  const scope = useCarousel((s) => s.scope);
  const { isMobile } = useMobile();
  const canvasRef = useRef(null);
  const sigRef = useRef('');
  const srcRef = useRef(null);

  const on = !scope || scope.on !== false;
  const kind = normalizeKind(scope && scope.kind);
  const small = mobile === undefined ? isMobile : mobile;
  const size = small ? BOX_MOBILE : BOX;
  const dpr = typeof window !== 'undefined' ? Math.min(2, window.devicePixelRatio || 1) : 1;
  const rectKey = rect ? [rect.x, rect.y, rect.w, rect.h].join(',') : '';
  const sig = [kind, watch === undefined ? '' : String(watch), filter, rectKey, size.w, dpr].join('|');

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    let raf = 0;
    let alive = true;
    const paint = () => {
      const data = source ? computeScope(source, kind, { filter, rect }) : null;
      draw(canvas, data, kind, { dpr });
    };
    // one measurement per animation frame at most: every dep change lands here,
    // and a re-run cancels the frame the last one asked for
    const frame = () => {
      raf = 0;
      if (!alive) return;
      if (live || sigRef.current !== sig || srcRef.current !== source) {
        sigRef.current = sig;
        srcRef.current = source;
        paint();
      }
      if (live) raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    // an <img> handed over before it decoded measures as nothing; try again
    const pending = source && !isPixels(source) && source.addEventListener && 'complete' in source && !source.complete
      ? source
      : null;
    const onLoad = () => {
      srcRef.current = null;
      if (!raf) raf = requestAnimationFrame(frame);
    };
    if (pending) pending.addEventListener('load', onLoad);
    return () => {
      alive = false;
      if (raf) cancelAnimationFrame(raf);
      if (pending) pending.removeEventListener('load', onLoad);
    };
  }, [sig, source, kind, filter, rect, live, dpr]);

  if (!on) return null;

  return (
    <div
      className={'cc-scope' + (small ? ' is-phone' : '') + (className ? ' ' + className : '')}
      style={{ width: size.w + 'px', height: size.h + 'px' }}
    >
      <canvas
        ref={canvasRef}
        className="cc-scope-face"
        width={Math.round(size.w * dpr)}
        height={Math.round(size.h * dpr)}
        style={{ width: size.w + 'px', height: size.h + 'px' }}
        role="img"
        aria-label={scopeLabel(kind) + ' of this photo'}
      />
      <div className="cc-scope-switch">
        {SCOPE_KINDS.map((k) => (
          <button
            key={k.key}
            type="button"
            className="cc-scope-pick"
            aria-pressed={k.key === kind}
            aria-label={k.label}
            title={k.label}
            onClick={() => setScope({ kind: k.key })}
          >
            {k.short}
          </button>
        ))}
      </div>
    </div>
  );
}
